/**
 * ControlEditorPage — Editor Táctil Mobile-First para PWA de Control.
 *
 * Modos (via ruta):
 *   /control/editor/new                → crear nueva noticia (publica en `articles`)
 *   /control/editor/article/:id        → editar una noticia ya publicada / borrador
 *   /control/editor/candidate/:id      → editar una candidata (imported_articles)
 *
 * Diseño:
 *   - Acordeones colapsables: Contenido / Clasificación / Imagen / Publicación / SEO
 *   - Botones táctiles ≥ 44×44px
 *   - Sticky bottom bar con [Guardar] [Publicar] respetando safe-area-inset-bottom (iOS)
 *   - Input `type=file accept=image/*` para galería nativa iOS/Android
 *
 * Reutiliza endpoints del Admin (MediaService S3, /admin/articles, /admin/authors,
 * /admin/categories, /admin/automation/imported/{id}). NO usa base64 en Mongo.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronDown, ChevronLeft, Loader2, ImagePlus, Trash2, Save, Play, AlertCircle, Sparkles,
} from "lucide-react";
import { controlApi, useOnlineStatus } from "../lib/controlApi";
import RichTextEditor from "@/components/admin/RichTextEditor";

// Plain-text bodies (no tags) → <p> paragraphs so the visual editor keeps their structure.
const toEditorHtml = (raw) => {
  const s = raw || "";
  if (!s.trim() || /<[a-z][\s\S]*>/i.test(s)) return s;
  const esc = (t) => t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return s.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
    .map((p) => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`).join("");
};

/* ============================================================
 * Accordion primitive
 * ============================================================ */
function Accordion({ title, defaultOpen = false, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-softblue bg-white" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full h-12 px-4 flex items-center justify-between text-left"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <span className="text-[11px] uppercase tracking-widest font-bold text-blueblack">
          {title}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-textgray transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="px-4 pb-4 pt-1 space-y-3 border-t border-softblue">{children}</div>}
    </div>
  );
}

function Label({ children, htmlFor }) {
  return (
    <label htmlFor={htmlFor} className="block text-[11px] uppercase tracking-widest font-bold text-blueblack mb-1">
      {children}
    </label>
  );
}

const inputCls = "w-full min-h-[44px] px-3 py-2 border border-softblue bg-white text-sm text-blueblack focus:outline-none focus:border-nightblue";

/* ============================================================
 * AI Assist button (inline, mobile-first)
 * ============================================================ */
function AIButton({ label, testId, loading, disabled, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading || disabled}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 min-h-[36px] px-3 border border-turquoise bg-turquoise/10 text-nightblue text-[11px] uppercase tracking-widest font-bold disabled:opacity-60"
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}

/* ============================================================
 * MAIN
 * ============================================================ */
export default function ControlEditorPage() {
  const { id } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const online = useOnlineStatus();

  // Determine mode from route
  const mode = useMemo(() => {
    if (loc.pathname.includes("/editor/candidate/")) return "candidate";
    if (loc.pathname.includes("/editor/article/")) return "article";
    return "new";
  }, [loc.pathname]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);

  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);

  // Form state (article schema)
  const [form, setForm] = useState({
    title: "",
    slug: "",
    summary: "",
    content: "",
    main_image: "",
    image_caption: "",
    image_credit: "",
    author_id: "",
    main_category_id: "",
    tags: [],
    status: "draft",
    is_featured: false,
    is_breaking: false,
    seo_title: "",
    seo_description: "",
    seo_keywords: [],
    content_type: "article",
    origin_type: "manual",
  });

  // Only for candidate mode: keep raw candidate to patch on save
  const [candidate, setCandidate] = useState(null);

  // ---- Load initial data ----
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [cats, auths] = await Promise.all([
          controlApi.listCategories().catch(() => []),
          controlApi.listAuthors().catch(() => []),
        ]);
        if (!alive) return;
        setCategories(cats || []);
        setAuthors(auths || []);

        if (mode === "article" && id) {
          const art = await controlApi.getArticle(id);
          if (!alive) return;
          setForm((f) => ({
            ...f,
            title: art.title || "",
            slug: art.slug || "",
            summary: art.summary || "",
            content: toEditorHtml(art.content),
            main_image: art.main_image || "",
            image_caption: art.image_caption || "",
            image_credit: art.image_credit || "",
            author_id: art.author_id || (auths[0] && auths[0].id) || "",
            main_category_id: art.main_category_id || (cats[0] && cats[0].id) || "",
            tags: art.tags || [],
            status: art.status || "draft",
            is_featured: !!art.is_featured,
            is_breaking: !!art.is_breaking,
            seo_title: art.seo_title || "",
            seo_description: art.seo_description || "",
            seo_keywords: art.seo_keywords || [],
            content_type: art.content_type || "article",
            origin_type: art.origin_type || "manual",
            publish_to_facebook: art.publish_to_facebook || "auto",
            publish_to_instagram: art.publish_to_instagram || "auto",
          }));
        } else if (mode === "candidate" && id) {
          const cand = await controlApi.getCandidate(id);
          if (!alive) return;
          setCandidate(cand);
          setForm((f) => ({
            ...f,
            title: cand.generated_title || cand.original_title || "",
            summary: cand.generated_summary || cand.original_summary || "",
            content: toEditorHtml(cand.generated_content || cand.original_content),
            main_image: cand.generated_image_url || cand.original_image_url || "",
            image_caption: cand.image_caption || "",
            image_credit: cand.image_credit || "",
            main_category_id: cand.main_category_id || cand.category_id || (cats[0] && cats[0].id) || "",
            tags: cand.generated_tags || [],
            author_id: cand.author_id || (auths[0] && auths[0].id) || "",
            status: "draft",
            content_type: "article",
            origin_type: "automated",
          }));
        } else {
          // NEW → pick sane defaults
          setForm((f) => ({
            ...f,
            author_id: (auths[0] && auths[0].id) || "",
            main_category_id: (cats[0] && cats[0].id) || "",
          }));
        }
      } catch (ex) {
        const msg = ex?.response?.data?.detail || ex?.message || "No se pudo cargar el editor";
        setError(typeof msg === "string" ? msg : "Error cargando datos");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, mode]);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  // ---- Image upload ----
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [aiBusy, setAiBusy] = useState(""); // "titles" | "summary" | "body_improve" | ""
  const [suggestedTitles, setSuggestedTitles] = useState([]);
  const onPickImage = () => fileRef.current && fileRef.current.click();
  const onFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // reset so same file can be re-picked
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("El archivo no es una imagen válida"); return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("La imagen supera los 15 MB"); return;
    }
    setUploading(true); setUploadPct(0);
    try {
      const r = await controlApi.uploadMedia(file, setUploadPct);
      if (r && r.url) {
        upd({ main_image: r.url });
        toast.success("Imagen subida");
      } else {
        toast.error("Respuesta inesperada del servidor");
      }
    } catch (ex) {
      const msg = ex?.response?.data?.detail || "No se pudo subir la imagen";
      toast.error(typeof msg === "string" ? msg : "Error al subir la imagen");
    } finally {
      setUploading(false); setUploadPct(0);
    }
  };

  // ---- AI assist ----
  const aiPayload = () => ({
    title: form.title || "",
    summary: form.summary || "",
    content: form.content || "",
    tags: (form.tags || []).join(", "),
    category:
      (categories.find((c) => c.id === form.main_category_id) || {}).slug || "",
    source_name: candidate ? candidate.source_name || "" : "",
    source_url: candidate ? candidate.source_url || "" : "",
  });

  const runAI = async (task) => {
    // Body-based tasks need the body; title/summary can work with title alone
    if (task === "body_improve") {
      if (!form.content || form.content.replace(/<[^>]+>/g, "").trim().length < 40) {
        toast.error("Escribí al menos 40 caracteres en el cuerpo para mejorar la redacción");
        return;
      }
    } else {
      if (!(form.title || "").trim() && !(form.content || "").trim()) {
        toast.error("Escribí un título o un cuerpo primero");
        return;
      }
    }
    if (!online) { toast.error("Necesitás conexión para usar la IA"); return; }
    setAiBusy(task);
    try {
      const r = await controlApi.aiAssist(task, aiPayload());
      const result = r?.result || {};
      if (task === "titles") {
        const list = result.titles || [];
        if (list.length === 0) { toast.error("La IA no devolvió titulares"); return; }
        setSuggestedTitles(list);
        toast.success("Titulares sugeridos");
      } else if (task === "summary") {
        if (!result.summary) { toast.error("La IA no devolvió bajada"); return; }
        upd({ summary: result.summary });
        toast.success("Bajada generada");
      } else if (task === "body_improve") {
        const html = result.body_html || result.body || "";
        if (!html) { toast.error("La IA no devolvió cuerpo"); return; }
        upd({ content: html });
        toast.success("Redacción mejorada");
      }
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.error?.message || "Error al consultar la IA");
      toast.error(msg);
    } finally {
      setAiBusy("");
    }
  };
  const validate = () => {
    if (!form.title.trim()) return "El título es obligatorio";
    if (!form.main_category_id) return "Elegí una categoría";
    if (!form.author_id) return "Elegí un autor";
    if (!form.content || form.content.replace(/<[^>]+>/g, "").trim().length < 20)
      return "El cuerpo es muy corto";
    return null;
  };

  const buildPayload = (targetStatus) => ({
    title: form.title.trim(),
    slug: form.slug || undefined,
    summary: form.summary || "",
    content: form.content || "",
    main_image: form.main_image || "",
    image_caption: form.image_caption || null,
    image_credit: form.image_credit || null,
    author_id: form.author_id,
    main_category_id: form.main_category_id,
    tags: form.tags,
    status: targetStatus,
    is_featured: !!form.is_featured,
    is_breaking: !!form.is_breaking,
    content_type: form.content_type,
    origin_type: form.origin_type,
    seo_title: form.seo_title || null,
    seo_description: form.seo_description || null,
    seo_keywords: form.seo_keywords || [],
    publish_to_facebook: form.publish_to_facebook || "auto",
    publish_to_instagram: form.publish_to_instagram || "auto",
  });

  const doSave = async (targetStatus) => {
    const err = validate();
    if (err) { toast.error(err); return; }
    if (!online) { toast.error("Necesitás conexión para guardar"); return; }
    const isPublish = targetStatus === "published";
    isPublish ? setPublishing(true) : setSaving(true);
    try {
      const payload = buildPayload(targetStatus);
      let created;
      if (mode === "article" && id) {
        // Update in place (article already exists)
        created = await controlApi.updateArticle(id, payload);
      } else if (mode === "candidate" && id) {
        // Two-phase for candidate: create new article with the edited content,
        // then mark the candidate as published (linked to the new article).
        created = await controlApi.createArticle(payload);
        try {
          await controlApi.patchCandidate(id, {
            status: "published",
            generated_title: payload.title,
            generated_summary: payload.summary,
            generated_content: payload.content,
            generated_image_url: payload.main_image || null,
            category_id: payload.main_category_id,
          });
        } catch (_e) { /* not blocking */ }
      } else {
        created = await controlApi.createArticle(payload);
      }
      toast.success(isPublish ? "Noticia publicada" : "Cambios guardados");
      // Redirect to detail (published) or back to list
      if (created && created.id) {
        if (isPublish || (created.status === "published")) {
          nav(`/control/articles/${created.id}`, { replace: true });
        } else {
          nav("/control/articles", { replace: true });
        }
      } else {
        nav(-1);
      }
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      let msg = "Error al guardar";
      if (typeof detail === "string") msg = detail;
      else if (detail && detail.error && detail.error.message) msg = detail.error.message;
      else if (detail && Array.isArray(detail)) msg = detail.map((d) => d.msg || d.message || "").join(" · ");
      toast.error(msg);
    } finally {
      setPublishing(false); setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-textgray text-sm flex items-center gap-2" data-testid="spc-editor-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando editor…
      </div>
    );
  }

  const title = mode === "new" ? "Nueva noticia"
    : mode === "candidate" ? "Editar candidata"
    : "Editar noticia";

  return (
    <div className="pb-32" data-testid="spc-editor">
      {/* Header */}
      <div className="px-4 pt-4 pb-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => nav(-1)}
          className="w-11 h-11 flex items-center justify-center -ml-2"
          aria-label="Volver"
          data-testid="spc-editor-back"
        >
          <ChevronLeft className="w-6 h-6 text-blueblack" />
        </button>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-textgray">Editor</div>
          <div className="font-serif text-xl font-bold text-blueblack leading-tight">{title}</div>
        </div>
      </div>

      {error && (
        <div className="mx-4 mb-3 p-3 border border-red-200 bg-red-50 text-red-700 text-xs flex items-start gap-2" data-testid="spc-editor-error">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>{error}</div>
        </div>
      )}

      <div className="px-4 space-y-2">
        {/* CONTENIDO */}
        <Accordion title="Contenido" defaultOpen testId="acc-contenido">
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <Label htmlFor="ed-title">Título</Label>
              <AIButton
                label="Sugerir titulares"
                testId="ed-ai-titles"
                loading={aiBusy === "titles"}
                onClick={() => runAI("titles")}
              />
            </div>
            <input
              id="ed-title"
              type="text"
              value={form.title}
              onChange={(e) => upd({ title: e.target.value })}
              className={inputCls}
              data-testid="ed-title"
              placeholder="Título de la noticia"
              autoComplete="off"
            />
            {suggestedTitles.length > 0 && (
              <div className="mt-2 border border-turquoise/40 bg-turquoise/5 p-2 space-y-1" data-testid="ed-ai-titles-list">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-textgray font-bold">
                  <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> Sugeridos</span>
                  <button type="button" onClick={() => setSuggestedTitles([])} className="text-textgray min-h-[32px] px-2">Cerrar</button>
                </div>
                {suggestedTitles.map((t, i) => (
                  <button
                    key={`t-${i}`}
                    type="button"
                    onClick={() => { upd({ title: t }); setSuggestedTitles([]); }}
                    className="block w-full text-left text-sm text-nightblue px-2 py-2 min-h-[44px] hover:bg-white/70"
                    data-testid={`ed-ai-title-pick-${i}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <Label htmlFor="ed-summary">Bajada</Label>
              <AIButton
                label="Generar bajada"
                testId="ed-ai-summary"
                loading={aiBusy === "summary"}
                onClick={() => runAI("summary")}
              />
            </div>
            <textarea
              id="ed-summary"
              value={form.summary}
              onChange={(e) => upd({ summary: e.target.value })}
              rows={3}
              className={inputCls + " resize-y"}
              data-testid="ed-summary"
              placeholder="Resumen o bajada (opcional)"
            />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <Label htmlFor="ed-content">Cuerpo</Label>
              <AIButton
                label="Mejorar redacción"
                testId="ed-ai-body-improve"
                loading={aiBusy === "body_improve"}
                onClick={() => runAI("body_improve")}
              />
            </div>
            <div id="ed-content-wrap" className="font-serif leading-relaxed" data-testid="ed-content-editor">
              <RichTextEditor
                value={form.content}
                onChange={(html) => upd({ content: html })}
                testId="ed-content"
                mediaLibrary={false}
                touch
                placeholder="Contenido de la noticia."
              />
            </div>
            <div className="text-[11px] text-textgray mt-1">
              {form.content.replace(/<[^>]+>/g, "").trim().length} caracteres
            </div>
          </div>
        </Accordion>

        {/* CLASIFICACIÓN */}
        <Accordion title="Clasificación" testId="acc-clasificacion">
          <div>
            <Label htmlFor="ed-cat">Categoría principal</Label>
            <select
              id="ed-cat"
              value={form.main_category_id}
              onChange={(e) => upd({ main_category_id: e.target.value })}
              className={inputCls}
              data-testid="ed-cat"
            >
              <option value="">— Elegí una categoría —</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="ed-author">Autor</Label>
            <select
              id="ed-author"
              value={form.author_id}
              onChange={(e) => upd({ author_id: e.target.value })}
              className={inputCls}
              data-testid="ed-author"
            >
              <option value="">— Elegí un autor —</option>
              {authors.map((a) => (
                <option key={a.id} value={a.id}>{a.name || a.email || a.id}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="ed-tags">Etiquetas (separadas por coma)</Label>
            <input
              id="ed-tags"
              type="text"
              value={(form.tags || []).join(", ")}
              onChange={(e) => upd({ tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
              className={inputCls}
              data-testid="ed-tags"
              placeholder="ushuaia, gobierno, salud"
              autoCapitalize="off"
              autoCorrect="off"
            />
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <label className="inline-flex items-center gap-2 min-h-[44px]">
              <input
                type="checkbox"
                className="w-5 h-5"
                checked={!!form.is_featured}
                onChange={(e) => upd({ is_featured: e.target.checked })}
                data-testid="ed-featured"
              />
              <span className="text-sm text-blueblack">Destacada</span>
            </label>
            <label className="inline-flex items-center gap-2 min-h-[44px]">
              <input
                type="checkbox"
                className="w-5 h-5"
                checked={!!form.is_breaking}
                onChange={(e) => upd({ is_breaking: e.target.checked })}
                data-testid="ed-breaking"
              />
              <span className="text-sm text-blueblack">Urgente</span>
            </label>
          </div>
        </Accordion>

        {/* IMAGEN */}
        <Accordion title="Imagen" testId="acc-imagen">
          {form.main_image ? (
            <div className="space-y-2">
              <img
                src={form.main_image}
                alt=""
                className="w-full aspect-video object-cover bg-slate-100 border border-softblue"
                data-testid="ed-image-preview"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onPickImage}
                  disabled={uploading}
                  className="flex-1 min-h-[44px] border border-softblue bg-white text-blueblack text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="ed-image-replace"
                >
                  <ImagePlus className="w-4 h-4" /> Reemplazar
                </button>
                <button
                  type="button"
                  onClick={() => upd({ main_image: "" })}
                  className="min-h-[44px] px-4 border border-red-300 bg-red-50 text-red-700 text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-2"
                  data-testid="ed-image-remove"
                >
                  <Trash2 className="w-4 h-4" /> Quitar
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={onPickImage}
              disabled={uploading}
              className="w-full min-h-[44px] h-40 border-2 border-dashed border-softblue bg-white text-textgray flex flex-col items-center justify-center gap-2 disabled:opacity-60"
              data-testid="ed-image-pick"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <div className="text-xs">Subiendo… {uploadPct}%</div>
                </>
              ) : (
                <>
                  <ImagePlus className="w-8 h-8" />
                  <div className="text-xs uppercase tracking-widest font-bold">Elegir de la galería</div>
                  <div className="text-[11px] text-textgray">JPG o PNG · hasta 15 MB</div>
                </>
              )}
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onFileChange}
            data-testid="ed-image-input"
          />
          <div>
            <Label htmlFor="ed-caption">Epígrafe</Label>
            <input
              id="ed-caption"
              type="text"
              value={form.image_caption || ""}
              onChange={(e) => upd({ image_caption: e.target.value })}
              className={inputCls}
              data-testid="ed-image-caption"
              placeholder="Ej: El intendente durante el anuncio."
            />
          </div>
          <div>
            <Label htmlFor="ed-credit">Crédito</Label>
            <input
              id="ed-credit"
              type="text"
              value={form.image_credit || ""}
              onChange={(e) => upd({ image_credit: e.target.value })}
              className={inputCls}
              data-testid="ed-image-credit"
              placeholder="Ej: Prensa Municipio"
            />
          </div>
        </Accordion>

        {/* PUBLICACIÓN */}
        <Accordion title="Publicación" testId="acc-publicacion">
          <div>
            <Label>Redes sociales</Label>
            <DistSelect label="Facebook" value={form.publish_to_facebook || "auto"}
              onChange={(v) => upd({ publish_to_facebook: v })} testId="ed-dist-fb" />
            <div className="h-2" />
            <DistSelect label="Instagram" value={form.publish_to_instagram || "auto"}
              onChange={(v) => upd({ publish_to_instagram: v })} testId="ed-dist-ig" />
            <div className="text-[11px] text-textgray mt-1">
              Auto: motor decide · Sí: fuerza publicación · No: bloquea.
            </div>
          </div>
        </Accordion>

        {/* SEO */}
        <Accordion title="SEO" testId="acc-seo">
          <div>
            <Label htmlFor="ed-seo-title">Título SEO</Label>
            <input
              id="ed-seo-title"
              type="text"
              value={form.seo_title || ""}
              onChange={(e) => upd({ seo_title: e.target.value })}
              className={inputCls}
              data-testid="ed-seo-title"
              placeholder="Si lo dejás vacío usa el título de la noticia"
            />
          </div>
          <div>
            <Label htmlFor="ed-seo-desc">Descripción SEO</Label>
            <textarea
              id="ed-seo-desc"
              value={form.seo_description || ""}
              onChange={(e) => upd({ seo_description: e.target.value })}
              rows={3}
              className={inputCls + " resize-y"}
              data-testid="ed-seo-description"
              placeholder="Hasta 160 caracteres."
              maxLength={200}
            />
          </div>
          <div>
            <Label htmlFor="ed-seo-kw">Palabras clave</Label>
            <input
              id="ed-seo-kw"
              type="text"
              value={(form.seo_keywords || []).join(", ")}
              onChange={(e) => upd({ seo_keywords: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
              className={inputCls}
              data-testid="ed-seo-keywords"
              placeholder="separá con comas"
              autoCapitalize="off"
            />
          </div>
        </Accordion>
      </div>

      {/* STICKY BOTTOM BAR */}
      <div
        className="fixed bottom-0 inset-x-0 z-40 border-t border-softblue bg-white/95 backdrop-blur max-w-screen-md mx-auto"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="spc-editor-bottom-bar"
      >
        <div className="grid grid-cols-2 gap-2 p-3">
          <button
            type="button"
            onClick={() => doSave("draft")}
            disabled={saving || publishing || uploading}
            className="min-h-[48px] border border-softblue bg-white text-blueblack text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="ed-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar
          </button>
          <button
            type="button"
            onClick={() => doSave("published")}
            disabled={saving || publishing || uploading}
            className="min-h-[48px] bg-emerald-600 text-white text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-emerald-700"
            data-testid="ed-publish-btn"
          >
            {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Publicar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 *  Reused distribution selector (mobile-first, 3 pills)
 * ============================================================ */
function DistSelect({ label, value, onChange, testId }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest font-bold text-blueblack mb-1">{label}</div>
      <div className="grid grid-cols-3 border border-softblue" data-testid={testId}>
        {["auto", "true", "false"].map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            className={`min-h-[44px] text-[11px] uppercase tracking-widest font-bold border-l border-softblue first:border-l-0 ${
              value === v ? "bg-blueblack text-white" : "bg-white text-blueblack"
            }`}
            data-testid={`${testId}-${v}`}
          >
            {v === "auto" ? "Auto" : v === "true" ? "Sí" : "No"}
          </button>
        ))}
      </div>
    </div>
  );
}
